import { Component, ViewChild, OnInit, Input, SimpleChanges, OnChanges, ChangeDetectorRef, ElementRef, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy, effect, inject, viewChild } from '@angular/core';
import { ActionIconDef, excelWorkbook, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig, GridRowCommitEvent, GridToolbarActionClickEvent } from '@inavista/components/grid';
import { ApiResponse } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { getCurrentDate } from 'projects/service/src/app/shared/utils/date-functions';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { environment } from 'projects/service/src/environments/environment';
import { CycleCountService } from '../../cycle-counts.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ADJUSTED_ACTION, ADJUSTED_ROW_COLOR, buildCostAnalysisActionIcons, buildCostAnalysisGridConfig, COST_ANALYSIS_COLUMNS,
  CostAnalysisGridContext, isAdjustedItem, ITEM_LINK_ACTION, LN_VAR_ACTION, RIMS_VAR_ACTION, UPLOAD_FILE_ACTION,
} from './cycle-counts-cost-analysis.grid.config';

const BASE_URL = environment.apiUrl;
// The old grid's content was window.innerHeight - 300 tall, with its toolbar and header above it and the totals row
// below. The inavista grid draws them inside its own box.
const GRID_CHROME_PX = 122;

@Component({
    selector: 'app-cycle-counts-cost-analysis',
    templateUrl: './cycle-counts-cost-analysis.component.html',
    styleUrls: ['./cycle-counts-cost-analysis.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.dataSource (the popup's itemList), not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class CycleCountCostAnalysisComponent implements OnInit, OnChanges, OnDestroy {
  showLoader: boolean = true;
  gridHeight: number = window.innerHeight - 300 + GRID_CHROME_PX;
  gridConfig: GridConfig = null;
  actionIcons: ActionIconDef[] = [];
  showPopup: boolean = false;
  @Input() costAnalysisList;
  @Input() warehouseId;
  @Input() status;
  @Input() cycleCountId;
  @Output() reloadPopupData: EventEmitter<any> = new EventEmitter();
  @Output() updatedcostAnalysisList: EventEmitter<any> = new EventEmitter();
  @Output() isFileUploaded: EventEmitter<any> = new EventEmitter();
  @Output() totalExtCost: EventEmitter<any> = new EventEmitter();
  @Output() totalCostDifference: EventEmitter<any> = new EventEmitter();

  private unsubscribe$ = new Subject<void>();

  BASE_URL = environment.apiUrl;
  showUploadDetailPopup: boolean = false
  uploadDetailData
  showColumnUnitCost: boolean = false
  showColumnCostVarLN: boolean = false
  showColumnExtCostLN: boolean = false
  canEditCostAnalysisGrid: boolean = false
  fileInvalid: boolean = false
  allowEdit: boolean = true
  fileUploaded = false;
  costDifference: number = 0
  dataSource
  showItemLinkPopup: boolean = false
  inventoryQuantityData = null
  refreshOnClick: boolean = true
  itemCode
  warehouse
  popupTitle
  openLinkSecondDescrption

  private readonly gridRef = viewChild<InavistaGrid>('cycleCountCostAnalysisGrid');
  get grid(): InavistaGrid { return this.gridRef(); }
  @ViewChild('fileInputRef', { static: false }) fileInputRef!: ElementRef;

  USER: any;
  private readonly gridRows = inject(LocalRowsGridApi);
  // The three toolbar checkboxes, as last applied.
  private toggles: Record<string, boolean> = { [RIMS_VAR_ACTION]: false, [LN_VAR_ACTION]: false, [ADJUSTED_ACTION]: false };

  constructor(
    private commonService: CommonService,
    private cycleCountService: CycleCountService,
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.USER = this.commonService.USER;
    this.showColumnUnitCost = this.commonService.roleClaims['CostAnalysisGrid_Column_UnitCost']?.visible // Except Techs
    this.showColumnCostVarLN = this.commonService.roleClaims['CostAnalysisGrid_Column_CostVarLN']?.visible // Except Techs
    this.showColumnExtCostLN = this.commonService.roleClaims['CostAnalysisGrid_Column_ExtCostLN']?.visible // Except Techs
    this.canEditCostAnalysisGrid = this.commonService.roleClaims['CostAnalysisGrid_AllowRowEdit']?.edit // only true for manager
    this.dataSource = this.costAnalysisList
    // VAR (RIMS), VAR (LN) and Adjusted: a click, or the values restored with the user's layout.
    effect(() => {
      const values = this.gridRef()?.toolbarValues() ?? {};
      this.applyToggles(values);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.dataSource = this.costAnalysisList
    this.updateList()
    this.refreshRows();
  }

  ngOnInit(): void {
    if (this.status !== 'Counted') {
      this.allowEdit = false
    } else {
      this.allowEdit = true
    }
    const ctx: CostAnalysisGridContext = {
      roleHidden: [
        ...(this.showColumnUnitCost ? [] : ['cost']),
        ...(this.showColumnExtCostLN ? [] : ['extCost']),
        ...(this.showColumnCostVarLN ? [] : ['costVariance']),
      ],
      canEdit: () => this.allowEdit,
      uploadDisabledReason: () => this.status !== 'In Progress' ? 'A file can be uploaded while the cycle count is In Progress.' : null,
      costDifference: () => this.costDifference,
      linkColor: row => (row?.['linkColorCode'] as string) || this.commonService.defaultLinkColor,
    };
    this.gridRows.columns.set(LocalRowsGridApi.schema(COST_ANALYSIS_COLUMNS));
    this.gridConfig = buildCostAnalysisGridConfig(ctx);
    this.actionIcons = buildCostAnalysisActionIcons(ctx);
    this.refreshRows();
    this.showLoader = false;
  }

  // The old VAR checkboxes filtered their column on "not equal 0"; Adjusted showed only the adjusted items (and, as
  // before, makes them the list the page recalculates and hands back).
  private applyToggles(values: Record<string, unknown>) {
    const next = {
      [RIMS_VAR_ACTION]: values[RIMS_VAR_ACTION] === true,
      [LN_VAR_ACTION]: values[LN_VAR_ACTION] === true,
      [ADJUSTED_ACTION]: values[ADJUSTED_ACTION] === true,
    };
    const changed = Object.keys(next).filter(k => next[k] !== this.toggles[k]);
    if (!changed.length) return;
    this.toggles = next;
    if (changed.includes(ADJUSTED_ACTION)) this.checkAdjusted(next[ADJUSTED_ACTION]);
    this.refreshRows();
  }

  // The rows the grid shows: dataSource, less the rows the VAR checkboxes leave out.
  refreshRows() {
    if (!this.gridConfig) return;
    const rows = (Array.isArray(this.dataSource) ? this.dataSource : [])
      .filter(item => !this.toggles[RIMS_VAR_ACTION] || item.rimsVarianceQty !== 0)
      .filter(item => !this.toggles[LN_VAR_ACTION] || item.lnVarianceQty !== 0);
    this.gridRows.rows.set(rows.map(item => ({ ...item })));
    this.grid?.refresh();
  }

  /** The page's own item a grid row stands for. */
  itemFor(row: any) {
    return (this.costAnalysisList ?? []).find(item => item.itemCode === row?.itemCode) ?? row;
  }

  disableEdit() {
    if (this.status !== 'Counted') {
      this.endEdit()
      this.allowEdit = false
    } else {
      this.allowEdit = true
    }
    // The lock changed: rows open only while the user may change them.
    if (this.gridConfig) this.gridConfig = { ...this.gridConfig, editMode: this.allowEdit ? 'inline' : 'none' };
  }

  // The tab was drawn while hidden: redraw it the first time it is opened, as before.
  public refreshOnce(): void {
    if (this.refreshOnClick) {
      this.grid?.refresh()
      this.refreshOnClick = false
    }
  }

  // The popup's Save: the open row is saved first, as the old endEdit did.
  public endEdit(): void {
    this.grid?.saveBatch()
  }

    showMasterItemCodePopup(eqData: any) {
        if (!eqData?.itemCode) return;
        const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
        panelClass: 'master-itemcode-popup-dialog',
        width: '80%',
        height: '80%',
        data: eqData
        });
        dialogRef.afterClosed().subscribe((result) => { });

    }

  updateList() {
    if (this.dataSource) {
      for (var item of this.dataSource) {
        if (item.actualQtyOnhand) {
          this.isFileUploaded.emit(true)
          this.fileUploaded = true
          break;
        }
      }
      let totalExtCost = 0
      let totalCostVariance = 0
      if (this.fileUploaded) {
        for (var item of this.dataSource) {
          let cost: string = item.cost != null ? parseFloat(item.cost).toFixed(2) : '0.00';
          let lnQtyOnhand: string = item.lnQtyOnhand != null ? parseFloat(item.lnQtyOnhand).toFixed(2) : '0.00';
          let extCost: number = parseFloat((parseFloat(cost) * parseFloat(lnQtyOnhand)).toFixed(2));
          item.extCost = extCost;
          totalExtCost += item.extCost;

          let actualQtyCorrected: number = item.actualQtyCorrected != null ? item.actualQtyCorrected : item.actualQtyOnhand;
          item.actualQtyCorrected = actualQtyCorrected
          let rimsQtyOnhand: string = item.rimsQtyOnhand != null ? parseFloat(item.rimsQtyOnhand).toFixed(2) : '0.00';
          item.rimsVarianceQty = actualQtyCorrected - parseFloat(rimsQtyOnhand);
          item.lnVarianceQty = actualQtyCorrected - parseFloat(lnQtyOnhand);

          let calculatedExtCost: number = actualQtyCorrected * parseFloat(cost);
          let parsedExtCost: number = isNaN(calculatedExtCost) ? 0.00 : extCost;
          item.costVariance = calculatedExtCost - parsedExtCost;
          totalCostVariance += item.costVariance
        }
        this.costDifference = totalExtCost != 0 ? ((totalCostVariance / totalExtCost) * 100) : null
        this.costDifference = this.costDifference ? parseFloat(this.costDifference.toFixed(2)) : null
        this.totalExtCost.emit(totalExtCost)
        this.totalCostDifference.emit(totalCostVariance)
      } else {
        for (var item of this.dataSource) {
          item.extCost = null;
          item.rimsVarianceQty = null
          item.lnVarianceQty = null
          item.costVariance = null
        }
      }
      this.updatedcostAnalysisList.emit(this.dataSource)
    }
  }

  // Enter or the ✓ icon (or the popup's Save): Adj Count and Comments go into the item, then the old
  // actionComplete('edit'): everything is recalculated and redrawn.
  onRowCommit(commit: GridRowCommitEvent) {
    const item = (this.dataSource ?? []).find(i => i.itemCode === commit.row['itemCode']) ?? this.itemFor(commit.row);
    if (!item) return;
    const qty = commit.row['actualQtyCorrected'];
    item.actualQtyCorrected = qty === '' || qty == null ? null : Number(qty);
    item.comments = commit.row['comments'];
    this.updateList()
    this.refreshRows()
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    if (event.iconKey === ITEM_LINK_ACTION) {
      const rowData = this.itemFor(event.row);
      setTimeout(() => {
        this.openLinkedOrderPopup({ rowData });
      }, 100);
    }
  }

  openLinkedOrderPopup(data) {
    this.itemCode = data.rowData.itemCode;
    this.warehouse = this.warehouseId;
    this.openLinkSecondDescrption = "Pending Inventory"
    this.popupTitle = this.itemCode + " " + data.rowData.itemDescription;
    this.showItemLinkPopup = true;
    this.showLoader = false;
  }

  onToolbarActionClick(event: GridToolbarActionClickEvent) {
    if (event.key === UPLOAD_FILE_ACTION && this.status === 'In Progress') {
      this.fileInputRef.nativeElement.click();
    }
  }

  // The old Excel export, "Cost Analysis(<date>).xlsx": the rows the grid shows, the visible columns, an adjusted item
  // in yellow (the old excelQueryCellInfo).
  async onExportRequested() {
    const rows = this.grid.visibleRows();
    const sheet = this.grid.excelSheet(rows);
    const sheetRows = sheet.rows.map((row, i) => isAdjustedItem(rows[i]) ? { ...row, fill: ADJUSTED_ROW_COLOR } : row);
    const blob = await excelWorkbook(sheetRows, sheet.columns, { sheetName: 'Cost Analysis' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Cost Analysis(${getCurrentDate()}).xlsx`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  errorMessage: string;

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.uploadCsvFile(file);
    }
    this.cdr.detectChanges();
  }


  uploadCsvFile(file) {
    const formData: FormData = new FormData();
    formData.append("file", file, file.name);
    formData.append('warehouse', this.warehouseId)
    const headers = new HttpHeaders({
      'Content-Type': `FileUpload`,
    });


    this.showLoader = true
    this.http.post(`${BASE_URL}CycleCount/ImportCycleCountDetail`, formData, { headers: headers }).pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: ApiResponse) => {
        if (res.isSuccessful) {
          this.showUploadDetailPopup = true
          this.uploadDetailData = res.result
          this.fileInvalid = this.checkUploadData(this.uploadDetailData)
          this.showLoader = false
          this.fileInputRef.nativeElement.value = ''
        } else {
          this.fileInputRef.nativeElement.value = ''
          this.commonService.showNotification('error', res.message);
          this.showLoader = false
        }
      }, (error: HttpErrorResponse) => {
        this.fileInputRef.nativeElement.value = ''
        this.commonService.showNotification('error', error.message);
        this.showLoader = false
        throw error;
      });

  }

  checkUploadData(uploadDetailData): boolean {
    let isFileInvalid = false;
    for (const detail of uploadDetailData) {
      if (!detail.warehouseIsValid || !detail.itemCodeIsValid || !detail.countIsValid) {
        isFileInvalid = true;
        break;
      }
    }
    return isFileInvalid;
  }

  onBrowseFile() {
    this.fileInputRef.nativeElement.click()
  }

  checkAdjusted(checked) {
    if (checked) {
      const filteredList = this.costAnalysisList.filter(item => item.actualQtyOnhand !== item.actualQtyCorrected);
      this.dataSource = filteredList
    } else {
      this.dataSource = this.costAnalysisList
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  onClose() {
    this.showPopup = false;
  }

}
